import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { bkAdmin } from "@/lib/booking";
import { Logo } from "@/components/site/Brand";
import { LayoutDashboard, Users, CalendarDays, FileText, Receipt, UserCog, Search, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/pro", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/pro/kunder", label: "Kunder", icon: Users },
  { to: "/pro/kalender", label: "Kalender", icon: CalendarDays },
  { to: "/pro/tilbud", label: "Tilbud", icon: FileText, admin: true },
  { to: "/pro/fakturaer", label: "Fakturaer", icon: Receipt, admin: true },
  { to: "/pro/ansatte", label: "Ansatte", icon: UserCog, admin: true },
  { to: "/pro/prospekter", label: "Finn kunder", icon: Search, admin: true },
  { to: "/admin", label: "Nettbestillinger", icon: Receipt, admin: true },
  { to: "/pro/innstillinger", label: "Innstillinger", icon: Settings, admin: true },
];

export const Layout = () => {
  const { user, signOut } = useAuth();
  const [platform, setPlatform] = useState(false);
  useEffect(() => { bkAdmin.overview().then(() => setPlatform(true)).catch(() => setPlatform(false)); }, []);
  const isAdmin = user?.role !== "employee";
  const items = NAV.filter((n) => (isAdmin || !n.admin) && (platform || n.to !== "/admin"));
  const link = ({ isActive }) =>
    `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background lg:flex">
      <aside className="lg:w-60 lg:shrink-0 lg:h-screen lg:sticky lg:top-0 border-b lg:border-b-0 lg:border-r border-border bg-card flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2 px-5 py-5 font-heading text-lg font-extrabold">
          <Logo className="h-9 w-9" />
          {platform ? "RenholdsPro" : "Prospect Finder"}
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:px-3 overflow-x-auto flex-1" data-testid="main-nav">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={link} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 lg:border-t border-border flex items-center gap-2">
          <div className="hidden lg:block min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.company_name}</div>
          </div>
          <button onClick={signOut} data-testid="logout-button" title="Logg ut" className="rounded-lg p-2 text-muted-foreground hover:bg-secondary">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0"><Outlet /></main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-end gap-3 mb-6">
    <div>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    <div className="ml-auto flex flex-wrap gap-2">{children}</div>
  </div>
);

export const kr = (n) => `${Number(n || 0).toLocaleString("nb-NO", { maximumFractionDigits: 0 })} kr`;
